import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import StripeTopUpFlow from './StripeTopUpFlow';
import { 
  Wallet,
  Plus, 
  Clock, 
  DollarSign, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Settings,
  CreditCard,
  Shield,
  Zap,
  AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const WalletSettings = () => {
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [autoRecharge, setAutoRecharge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTopUp, setShowTopUp] = useState(false);
  const [showAutoRecharge, setShowAutoRecharge] = useState(false);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch wallet balance
      const balanceResponse = await fetch(`${BACKEND_URL}/api/wallet/balance`, {
        credentials: 'include'
      });
      
      if (balanceResponse.ok) {
        const balanceData = await balanceResponse.json();
        setBalance(balanceData);
      }

      // Fetch transaction history
      const transactionsResponse = await fetch(`${BACKEND_URL}/api/wallet/transactions?limit=10`, {
        credentials: 'include'
      });

      if (transactionsResponse.ok) {
        const transactionsData = await transactionsResponse.json();
        setTransactions(transactionsData.transactions || []);
      }

      // Fetch auto-recharge config
      const autoRechargeResponse = await fetch(`${BACKEND_URL}/api/wallet/auto-recharge`, {
        credentials: 'include'
      });

      if (autoRechargeResponse.ok) {
        const autoRechargeData = await autoRechargeResponse.json();
        setAutoRecharge(autoRechargeData);
      }

    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setError('Failed to load wallet information');
    } finally {
      setLoading(false);
    }
  };

  const handleTopUpSuccess = async (paymentIntent) => {
    console.log('Payment successful:', paymentIntent);
    // Refresh wallet data
    await fetchWalletData();
    setShowTopUp(false);
  };

  const handleTopUpError = (error) => {
    console.error('Payment error:', error);
    setError(error || 'Payment failed');
  };

  const handleAutoRechargeUpdate = async () => {
    setActionLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/wallet/auto-recharge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(autoRecharge)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update auto-recharge settings');
      }

      alert('Auto-recharge settings updated successfully!');
      setShowAutoRecharge(false);

    } catch (err) {
      console.error('Auto-recharge update error:', err);
      setError(err.message || 'Failed to update auto-recharge settings');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'low_balance': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'blocked': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'low_balance': return <AlertTriangle className="w-4 h-4" />;
      case 'blocked': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit':
      case 'topup': 
        return <Plus className="w-4 h-4 text-green-600" />;
      case 'debit':
      case 'usage':
        return <TrendingUp className="w-4 h-4 text-blue-600" />;
      case 'auto_recharge':
        return <Zap className="w-4 h-4 text-purple-600" />;
      default:
        return <DollarSign className="w-4 h-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading wallet information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Wallet</h2>
          <p className="text-gray-600 dark:text-gray-300">Manage your prepaid wallet balance and settings</p>
        </div>
        <Button onClick={fetchWalletData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Wallet Balance */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Wallet className="w-5 h-5" />
            Current Balance
          </h3>
          {balance && (
            <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(balance.status)}`}>
              {getStatusIcon(balance.status)}
              {balance.status?.charAt(0).toUpperCase() + balance.status?.slice(1).replace('_', ' ')}
            </span>
          )}
        </div>

        {balance ? (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  ${balance.balance_usd.toFixed(2)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  Available Balance
                </div>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Low balance alert at ${(balance.low_balance_threshold_cents / 100).toFixed(2)}
                </div>
              </div>

              <div className="space-y-2">
                <Button 
                  onClick={() => setShowTopUp(true)}
                  className="w-full"
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Add Funds
                </Button>
                <Button 
                  onClick={() => setShowAutoRecharge(true)}
                  variant="outline" 
                  size="sm"
                  className="w-full"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Auto-Recharge Settings
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Wallet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">Wallet Not Available</h4>
            <p className="text-gray-600 mb-4 dark:text-gray-300">
              Unable to load wallet information. Please try refreshing.
            </p>
          </div>
        )}
      </div>

      {/* Auto-Recharge Status */}
      {autoRecharge && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-gray-900 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-gray-100 flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Auto-Recharge
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className={`font-medium ${autoRecharge.is_enabled ? 'text-green-600' : 'text-gray-600'}`}>
                    {autoRecharge.is_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {autoRecharge.is_enabled && (
                  <>
                    <div className="flex justify-between">
                      <span>Trigger at:</span>
                      <span className="font-medium">${autoRecharge.trigger_threshold_usd.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Recharge amount:</span>
                      <span className="font-medium">${autoRecharge.recharge_amount_usd.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Monthly budget:</span>
                      <span className="font-medium">${autoRecharge.monthly_budget_limit_usd.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex justify-between">
                  <span>This month recharges:</span>
                  <span className="font-medium">{autoRecharge.current_month_recharges}</span>
                </div>
                <div className="flex justify-between">
                  <span>This month amount:</span>
                  <span className="font-medium">${autoRecharge.current_month_amount_usd.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-gray-900 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-gray-100">Recent Transactions</h3>
        
        {transactions.length > 0 ? (
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  {getTransactionIcon(transaction.transaction_type)}
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {transaction.description}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {formatDate(transaction.created_at)}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-medium ${transaction.amount_cents > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {transaction.amount_cents > 0 ? '+' : ''}${Math.abs(transaction.amount_usd).toFixed(2)}
                  </div>
                  <div className={`text-xs px-2 py-1 rounded ${
                    transaction.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                    transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                    'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    {transaction.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">No Transactions</h4>
            <p className="text-gray-600 dark:text-gray-300">
              Your wallet transaction history will appear here.
            </p>
          </div>
        )}
      </div>

      {/* Stripe Top-up Flow */}
      <StripeTopUpFlow
        isOpen={showTopUp}
        onClose={() => setShowTopUp(false)}
        onSuccess={handleTopUpSuccess}
        onError={handleTopUpError}
        initialAmount={25.00}
      />

      {/* Auto-recharge Modal */}
      {showAutoRecharge && autoRecharge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg dark:bg-gray-900">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-gray-100">
              Auto-Recharge Settings
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="auto-recharge-enabled"
                  checked={autoRecharge.is_enabled}
                  onChange={(e) => setAutoRecharge({...autoRecharge, is_enabled: e.target.checked})}
                  className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="auto-recharge-enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable Auto-Recharge
                </label>
              </div>
              
              {autoRecharge.is_enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Trigger when balance falls below (USD)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="100"
                      step="0.01"
                      value={autoRecharge.trigger_threshold_usd}
                      onChange={(e) => setAutoRecharge({
                        ...autoRecharge, 
                        trigger_threshold_usd: parseFloat(e.target.value) || 0,
                        trigger_threshold_cents: Math.round((parseFloat(e.target.value) || 0) * 100)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Recharge amount (USD)
                    </label>
                    <input
                      type="number"
                      min="10"
                      max="100"
                      step="0.01"
                      value={autoRecharge.recharge_amount_usd}
                      onChange={(e) => setAutoRecharge({
                        ...autoRecharge, 
                        recharge_amount_usd: parseFloat(e.target.value) || 0,
                        recharge_amount_cents: Math.round((parseFloat(e.target.value) || 0) * 100)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Monthly budget limit (USD)
                    </label>
                    <input
                      type="number"
                      min="25"
                      max="1000"
                      step="0.01"
                      value={autoRecharge.monthly_budget_limit_usd}
                      onChange={(e) => setAutoRecharge({
                        ...autoRecharge, 
                        monthly_budget_limit_usd: parseFloat(e.target.value) || 0,
                        monthly_budget_limit_cents: Math.round((parseFloat(e.target.value) || 0) * 100)
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                    />
                  </div>
                </>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleAutoRechargeUpdate}
                disabled={actionLoading}
                className="flex-1"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Settings className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </Button>
              <Button
                onClick={() => setShowAutoRecharge(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletSettings;